import { Optional } from "@/domains/common/types";
import { GLOBAL_FALLBACK_UUID } from "@/domains/global/identifiers";
import { uuidModule } from "@/modules/uuid";
import {
  BaseSyncOperation,
  BaseSyncOperationParams,
} from "@/store/sync/operations/BaseSyncOperation";
import { generateDefaultOwnerScopes } from "@/store/sync/operations/helpers/common";
import { ICreateSourceOperation } from "@/store/sync/operations/types";
import { OptimisticSyncUpdate, SyncModelData, SyncOperationKind } from "@/store/sync/types";
import { SourceKind, SourceModelData, SourceUpsertedSyncUpdateValue } from "@/store/sources/types";

export class CreateSourceOperation extends BaseSyncOperation<ICreateSourceOperation> {
  get operationKind(): SyncOperationKind {
    return "CREATE_SOURCE";
  }

  constructor({
    store,
    payload: providedPayload,
    ...params
  }: Omit<BaseSyncOperationParams<ICreateSourceOperation>, "payload"> & {
    payload: Optional<
      Omit<ICreateSourceOperation["payload"], "associated_artifact_id">,
      "associated_audio_recording_id" | "secondary_label"
    >;
  }) {
    const payload = {
      ...providedPayload,
      associated_artifact_id: GLOBAL_FALLBACK_UUID,
      associated_audio_recording_id: providedPayload.associated_audio_recording_id ?? null,
      secondary_label: providedPayload.secondary_label ?? null,
    };

    super({
      ...params,
      store,
      payload,
    });
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const sourceKind = this.payload.source_kind as SourceKind;
    const sourceValue: SourceUpsertedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "SOURCE",
      model_version: 0,
      model_data: {
        id: this.payload.id,
        source_kind: sourceKind,
        associated_artifact_id: this.payload.associated_artifact_id,
        associated_audio_recording_id: this.payload.associated_audio_recording_id ?? null,
        primary_label: this.payload.primary_label,
        secondary_label: this.payload.secondary_label ?? null,
      },
      model_scopes: [generateDefaultOwnerScopes({ store: this.store })],
    };

    const sourceUpserted: OptimisticSyncUpdate<SourceModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "UPSERTED",
      value: sourceValue,
    };

    return [sourceUpserted];
  }

  async triggerRecompute(): Promise<void> {
    await this.store.sources.recompute(this.payload.id);
  }
}
